import React from "react";
import PropTypes from "prop-types";
import { Controller } from "react-hook-form";
import { Input, InputNumber } from "antd";
import { useTranslation } from "react-i18next";
import {
  getFormLabel,
  getFormPlaceholder,
  getFormInstructions,
} from "src/helpers/multilingualHelper";
import Span from "src/components/ui/Span";

/**
 * Tek bir RHF + antd Input/TextArea/Password/InputNumber sarmalayıcısı.
 * Renk, focus, hata stili tamamen antd ConfigProvider token'larından gelir
 * (`AntdConfigProvider` + `clientThemeFromSettings.js`).
 *
 * Mevcut KYX `*ClientForm` alanları ile aynı `props` sözleşmesini taşır:
 * `elementId`, `label`, `name`, `required`, `placeholder`, `control`,
 * `errors`, `config`, `onChange`, `value`. Buna ek olarak `variant` ile
 * input türü seçilir.
 */
export const TextField = ({
  elementId,
  label,
  name,
  required,
  disabled,
  placeholder,
  control,
  errors,
  config,
  onChange,
  value,
  variant = "text",
  inputMode,
  autoComplete,
  rules,
  rows = 4,
  numberFormatter,
  numberParser,
  errorMessage,
  className,
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || "en";

  const displayLabel = getFormLabel(label, currentLanguage);
  const displayPlaceholder = getFormPlaceholder(placeholder, currentLanguage);
  const displayInstructions = getFormInstructions(
    config?.instructions,
    currentLanguage
  );

  const fieldName = name || elementId;
  const hasError = !!errors?.[elementId];
  const status = hasError ? "error" : "";

  if (disabled && variant === "text") {
    return null;
  }

  const renderControl = (field) => {
    if (!field.value && value) {
      field.onChange(value);
    }

    const handleChange = (next) => {
      field.onChange(next);
      if (onChange) {
        const eventLike =
          next && typeof next === "object" && "target" in next
            ? next
            : { target: { value: next } };
        onChange(elementId, eventLike);
      }
    };

    const commonProps = {
      id: elementId,
      placeholder: displayPlaceholder,
      disabled,
      status,
      size: "large",
      className,
      value: field.value ?? "",
      onBlur: field.onBlur,
      ref: field.ref,
    };

    if (variant === "textarea") {
      return (
        <Input.TextArea
          {...commonProps}
          rows={rows}
          onChange={(e) => handleChange(e)}
        />
      );
    }

    if (variant === "password") {
      return (
        <Input.Password
          {...commonProps}
          autoComplete={autoComplete || "current-password"}
          onChange={(e) => handleChange(e)}
        />
      );
    }

    if (variant === "number") {
      const numberValue =
        field.value === "" || field.value == null ? null : field.value;

      return (
        <InputNumber
          id={elementId}
          placeholder={displayPlaceholder}
          disabled={disabled}
          status={status}
          size="large"
          className={`${className || ""} w-full`.trim()}
          value={numberValue}
          onBlur={field.onBlur}
          ref={field.ref}
          stringMode
          formatter={numberFormatter}
          parser={numberParser}
          onChange={(next) => handleChange(next)}
        />
      );
    }

    /** text / email / tel / url — antd Input htmlType ile native semantik korunur */
    return (
      <Input
        {...commonProps}
        type={variant}
        inputMode={inputMode}
        autoComplete={autoComplete}
        onChange={(e) => handleChange(e)}
      />
    );
  };

  return (
    <div className="relative inline-block w-full">
      {displayLabel && (
        <label
          htmlFor={elementId}
          className={`block ${config?.hasInstructions ? "" : "mb-2"} text-sm text-gray-700`}
        >
          {displayLabel}
          <span className="notranslate">{required && "*"}</span>
        </label>
      )}
      {config?.hasInstructions && displayInstructions && (
        <label className="mb-2 text-xs text-gray-300">
          {displayInstructions}
        </label>
      )}
      <Controller
        name={fieldName}
        control={control}
        rules={{ required, ...(rules || {}) }}
        render={({ field }) => renderControl(field)}
      />
      {hasError && (
        <Span className="text-FailRed400 text-xxs">
          {errors[elementId]?.message ||
            errorMessage ||
            t("form.thisFieldRequired")}
        </Span>
      )}
    </div>
  );
};

TextField.propTypes = {
  elementId: PropTypes.string.isRequired,
  label: PropTypes.any,
  name: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.any,
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  config: PropTypes.object,
  onChange: PropTypes.func,
  value: PropTypes.any,
  variant: PropTypes.oneOf([
    "text",
    "email",
    "url",
    "tel",
    "password",
    "number",
    "textarea",
  ]),
  inputMode: PropTypes.string,
  autoComplete: PropTypes.string,
  rules: PropTypes.object,
  rows: PropTypes.number,
  numberFormatter: PropTypes.func,
  numberParser: PropTypes.func,
  errorMessage: PropTypes.string,
  className: PropTypes.string,
};

export default TextField;
